"use client"

import React from "react"

import { useState } from "react"
import { ArrowRight, Mail, Linkedin } from "lucide-react"

export function Contact() {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    organisation: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000))
    setIsSubmitting(false)
    setIsSubmitted(true)
  }

  return (
    <section id="connect" className="py-24 lg:py-32 border-t border-border/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left column - Info */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-primary" />
              <span className="text-xs tracking-[0.3em] uppercase text-muted-foreground font-medium">
                Let&apos;s Connect
              </span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
              Open to opportunities
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              I&apos;m open to Non-Executive Director, advisory and senior stakeholder 
              engagement opportunities, particularly where organisations are navigating 
              cyber risk, trust, governance or growth.
            </p>

            <div className="p-6 border border-border/50 bg-card mb-12">
              <h3 className="text-sm font-medium text-foreground mb-4">Areas of interest</h3>
              <ul className="space-y-2">
                {[
                  "Non-Executive Director roles",
                  "Advisory and consulting engagements",
                  "Senior stakeholder engagement",
                  "Cyber risk and governance",
                  "Business development and growth",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="w-1.5 h-1.5 bg-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact details */}
            <div className="space-y-6">
              <a 
                href="mailto:nick.bell@cyber-risk-solutions.co.uk" 
                className="flex items-center gap-4 text-muted-foreground hover:text-primary transition-colors group"
              >
                <div className="w-12 h-12 border border-border flex items-center justify-center group-hover:border-primary/50 transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span className="text-sm tracking-wide">nick.bell@cyber-risk-solutions.co.uk</span>
              </a>
              <a 
                href="https://www.linkedin.com/in/nick-bell-c-r-s"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-muted-foreground hover:text-primary transition-colors group"
              >
                <div className="w-12 h-12 border border-border flex items-center justify-center group-hover:border-primary/50 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </div>
                <span className="text-sm tracking-wide">Connect on LinkedIn</span>
              </a>
            </div>
          </div>

          {/* Right column - Form */}
          <div>
            {isSubmitted ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 border border-primary/50 flex items-center justify-center mx-auto mb-6">
                    <ArrowRight className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-serif text-2xl mb-3 text-foreground">Message Received</h3>
                  <p className="text-muted-foreground">
                    Thank you for reaching out. I&apos;ll respond within 24 hours.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-xs tracking-widest uppercase text-muted-foreground">
                      Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                      className="w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-xs tracking-widest uppercase text-muted-foreground">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={formState.email}
                      onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      className="w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="organisation" className="text-xs tracking-widest uppercase text-muted-foreground">
                    Organisation
                  </label>
                  <input
                    id="organisation"
                    type="text"
                    value={formState.organisation}
                    onChange={(e) => setFormState({ ...formState, organisation: e.target.value })}
                    className="w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none transition-colors"
                    placeholder="Your organisation (optional)"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-xs tracking-widest uppercase text-muted-foreground">
                    Message
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    className="w-full bg-transparent border-b border-border py-3 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="How can I help?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-primary text-primary-foreground text-sm font-medium tracking-wide hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
